/**
 * PipelineBar — FTP's grey "Pipeline Type:" bar at the top of the right side
 * of Admin > Stage Pipeline: the picker (Default, then the account's
 * pipelines), rename (every pipeline but Default; FTP's "Update") and add.
 * PlanTypesLine is the "Plan Types:" line under it: the live plan types on the
 * selected pipeline, or "No Plan Types".
 */
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { Select } from '@/components/ui/Select';
import { DEFAULT_PIPELINE_ID, type Pipeline, type PipelinePlanType } from '@/services/stagePipelineAdmin.service';

export const STAGE_PIPELINE_BAR_CLASS = 'flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-200 bg-slate-100 px-3 py-2 text-sm';

export interface PipelineBarProps {
  pipelines: Pipeline[];
  pipelineId: number;
  onPipelineChange: (id: number) => void;
  onRename: (pipeline: Pipeline) => void;
  onAdd: () => void;
}

export function PipelineBar({ pipelines, pipelineId, onPipelineChange, onRename, onAdd }: PipelineBarProps) {
  const current = pipelines.find((p) => p.id === pipelineId);
  return (
    <div data-testid="pipeline-bar" className={STAGE_PIPELINE_BAR_CLASS}>
      <label htmlFor="stage-pipeline-type" className="font-medium text-slate-700">
        Pipeline Type:
      </label>
      <Select<number>
        id="stage-pipeline-type"
        options={pipelines.map((p) => ({ value: p.id, label: p.name }))}
        value={pipelineId}
        onChange={(v) => {
          if (v !== null && v !== undefined) onPipelineChange(v);
        }}
        wrapperClassName="w-56"
        className="h-8 bg-white py-1"
      />
      {current && current.id !== DEFAULT_PIPELINE_ID && (
        <AdminRowAction kind="edit" title="Rename pipeline" label={`Rename pipeline ${current.name}`} onClick={() => onRename(current)} />
      )}
      <AdminRowAction kind="add" title="Add pipeline" label="Add pipeline" onClick={onAdd} />
    </div>
  );
}

/** The plan types on the selected pipeline; null while they load. */
export function PlanTypesLine({ planTypes }: { planTypes: PipelinePlanType[] | null }) {
  return (
    <div data-testid="plan-types-line" className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-600">
      <span className="font-semibold text-slate-700">Plan Types:</span>{' '}
      {planTypes === null ? '…' : planTypes.length === 0 ? 'No Plan Types' : planTypes.map((t) => t.name).join(', ')}
    </div>
  );
}
